import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import {
    createCostPriceApi,
    deleteCostPriceApi,
    getMerchantProductDetailApi,
    getMerchantProductsActivityApi,
    getMerchantProductsByMerchantApi,
    listCostPricesApi,
    updateCostPriceApi,
} from "@src/api";
import type {
    CostPricePayload,
    CostPriceType,
    ErrorResponseType,
    MerchantProductDetailType,
    MerchantProductsActivityResponse,
    MerchantProductsQueryParams,
    MerchantProductsResponse,
} from "@src/types";
import { useAppMessage } from "@repo/ui";

export interface UseMerchantProductsParams {
    merchantId?: string;
    page?: number;
    size?: number;
    search?: string | null;
    available?: boolean | null;
    brand?: string | null;
    isBrand?: boolean | null;
    enabled?: boolean;
}

function normalizeString(value?: string | null): string | undefined {
    if (value === null || value === undefined) return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

export function useMerchantProducts({
    merchantId,
    page = 1,
    size = 10,
    search,
    available,
    brand,
    isBrand,
    enabled = true,
}: UseMerchantProductsParams) {
    const params = useMemo<MerchantProductsQueryParams>(() => {
        return {
            page,
            size,
            search: normalizeString(search),
            available: available ?? undefined,
            brand: normalizeString(brand),
            is_brand: isBrand ?? undefined,
        };
    }, [available, brand, isBrand, page, search, size]);

    const canFetch = enabled && Boolean(merchantId);

    return useQuery<MerchantProductsResponse, AxiosError<ErrorResponseType>>({
        queryKey: ["merchant-products", merchantId ?? "none", params],
        queryFn: ({ signal }) =>
            getMerchantProductsByMerchantApi(merchantId as string, params, signal),
        enabled: canFetch,
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
    });
}

export interface UseMerchantProductsActivityParams {
    merchantId?: string;
    page?: number;
    size?: number;
    search?: string | null;
    available?: boolean | null;
    brand?: string | null;
    brandFlag?: boolean | null;
    isBrand?: boolean | null;
    enabled?: boolean;
}

export function useMerchantProductsActivity({
    merchantId,
    page = 1,
    size = 50,
    search,
    available,
    brand,
    brandFlag,
    isBrand,
    enabled = true,
}: UseMerchantProductsActivityParams) {
    const params = useMemo<MerchantProductsQueryParams>(() => {
        return {
            page,
            size,
            search: normalizeString(search),
            available: available ?? undefined,
            brand: normalizeString(brand),
            brandFlag: brandFlag ?? undefined,
            is_brand: isBrand ?? undefined,
        };
    }, [available, brand, brandFlag, isBrand, page, search, size]);

    const canFetch = enabled && Boolean(merchantId);

    return useQuery<MerchantProductsActivityResponse, AxiosError<ErrorResponseType>>({
        queryKey: ["merchant-products-activity", merchantId ?? "none", params],
        queryFn: ({ signal }) =>
            getMerchantProductsActivityApi(merchantId as string, params, signal),
        enabled: canFetch,
        placeholderData: (previousData) => previousData,
        refetchOnWindowFocus: false,
    });
}

export interface UseMerchantProductDetailParams {
    merchantProductId?: string;
    enabled?: boolean;
}

export function useMerchantProductDetail({
    merchantProductId,
    enabled = true,
}: UseMerchantProductDetailParams) {
    const canFetch = enabled && Boolean(merchantProductId);

    return useQuery<MerchantProductDetailType, AxiosError<ErrorResponseType>>({
        queryKey: ["merchant-product-detail", merchantProductId ?? "none"],
        queryFn: ({ signal }) =>
            getMerchantProductDetailApi(merchantProductId as string, signal),
        enabled: canFetch,
        refetchOnWindowFocus: false,
    });
}

interface UseCostPricesParams {
    merchantProductId?: string;
    enabled?: boolean;
}

export function useCostPrices({ merchantProductId, enabled = true }: UseCostPricesParams) {
    const canFetch = enabled && Boolean(merchantProductId);

    return useQuery<CostPriceType[], AxiosError<ErrorResponseType>>({
        queryKey: ["merchant-product-cost-prices", merchantProductId ?? "none"],
        queryFn: ({ signal }) => listCostPricesApi(merchantProductId as string, signal),
        enabled: canFetch,
        refetchOnWindowFocus: false,
    });
}

export function useCostPriceCreate(merchantProductId: string) {
    const queryClient = useQueryClient();
    const message = useAppMessage();

    return useMutation<CostPriceType, AxiosError<ErrorResponseType>, CostPricePayload>({
        mutationKey: ["merchant-product-cost-price", "create", merchantProductId],
        mutationFn: (payload) => createCostPriceApi(merchantProductId, payload),
        onSuccess: () => {
            message.success("Себестоимость добавлена");
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-cost-prices", merchantProductId],
            });
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-detail", merchantProductId],
            });
        },
        onError: (error) => {
            message.error(error.response?.data?.detail ?? "Не удалось добавить себестоимость");
        },
    });
}

interface UpdateCostPriceVariables {
    costPriceId: string;
    payload: CostPricePayload;
}

export function useCostPriceUpdate(merchantProductId: string) {
    const queryClient = useQueryClient();
    const message = useAppMessage();

    return useMutation<CostPriceType, AxiosError<ErrorResponseType>, UpdateCostPriceVariables>({
        mutationKey: ["merchant-product-cost-price", "update", merchantProductId],
        mutationFn: ({ costPriceId, payload }) => updateCostPriceApi(costPriceId, payload),
        onSuccess: () => {
            message.success("Себестоимость обновлена");
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-cost-prices", merchantProductId],
            });
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-detail", merchantProductId],
            });
        },
        onError: (error) => {
            message.error(error.response?.data?.detail ?? "Не удалось обновить себестоимость");
        },
    });
}

interface DeleteCostPriceVariables {
    costPriceId: string;
}

export function useCostPriceDelete(merchantProductId: string) {
    const queryClient = useQueryClient();
    const message = useAppMessage();

    return useMutation<void, AxiosError<ErrorResponseType>, DeleteCostPriceVariables>({
        mutationKey: ["merchant-product-cost-price", "delete", merchantProductId],
        mutationFn: ({ costPriceId }) => deleteCostPriceApi(costPriceId),
        onSuccess: () => {
            message.success("Себестоимость удалена");
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-cost-prices", merchantProductId],
            });
            queryClient.invalidateQueries({
                queryKey: ["merchant-product-detail", merchantProductId],
            });
        },
        onError: (error) => {
            message.error(error.response?.data?.detail ?? "Не удалось удалить себестоимость");
        },
    });
}
